import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { MessageCircle, X, Send, Sparkles, MapPin } from 'lucide-react';
import axios from 'axios';

const API = '';

const ChatbotWidget = () => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'assistant', content: "Hi! I'm the Discover Essex assistant. Ask me anything — like \"Find me a plumber in Chelmsford\" or \"What events are on this weekend?\"" }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  // Admin-controlled display settings. `enabled=false` hides the widget
  // everywhere; `scope='home'` restricts to `/` only (legacy default);
  // `scope='all'` shows on every route. Default while fetching = enabled+home
  // so a brief cold-start doesn't flash the FAB on non-home routes.
  const [displayCfg, setDisplayCfg] = useState({ enabled: true, scope: 'home' });

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) inputRef.current.focus();
  }, [isOpen]);

  useEffect(() => {
    axios.get(`${API}/api/chatbot/public-settings`)
      .then(r => setDisplayCfg({ enabled: !!r.data.enabled, scope: r.data.scope || 'home' }))
      .catch(() => {});
  }, []);

  // Respect admin toggle + scope
  if (!displayCfg.enabled) return null;
  if (displayCfg.scope === 'home' && location.pathname !== '/') return null;

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;

    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: text }]);
    setLoading(true);

    try {
      const res = await axios.post(`${API}/api/chatbot/message`, {
        message: text,
        session_id: sessionId,
      });
      setSessionId(res.data.session_id);
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: "Sorry, I couldn't process that. Try again in a moment!" }]);
    }
    setLoading(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const suggestions = [
    "Restaurants in Chelmsford",
    "Plumber near Southend",
    "Events this weekend",
    "Hair salon in Colchester",
  ];

  return (
    <>
      {/* FAB Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-24 md:bottom-6 right-4 md:right-6 z-50 w-14 h-14 bg-[#0F2C3E] dark:bg-[#D4A017] rounded-full shadow-[0_4px_24px_rgba(15,44,62,0.4)] hover:shadow-[0_4px_32px_rgba(15,44,62,0.6)] flex items-center justify-center transition-all hover:scale-105 active:scale-95"
          data-testid="chatbot-fab"
        >
          <MessageCircle className="h-6 w-6 text-white dark:text-[#0F2C3E]" />
        </button>
      )}

      {/* Chat Panel */}
      {isOpen && (
        <div className="fixed bottom-24 md:bottom-6 right-4 md:right-6 z-50 w-[380px] max-w-[calc(100vw-2rem)] h-[520px] max-h-[calc(100vh-8rem)] md:max-h-[calc(100vh-6rem)] bg-white dark:bg-[#1a1a1a] rounded-2xl shadow-[0_8px_48px_rgba(0,0,0,0.2)] flex flex-col overflow-hidden border border-slate-200 dark:border-slate-700" data-testid="chatbot-panel">
          {/* Header */}
          <div className="bg-[#0F2C3E] px-4 py-3 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 bg-[#D4A017]/20 rounded-lg flex items-center justify-center">
                <Sparkles className="h-4 w-4 text-[#D4A017]" />
              </div>
              <div>
                <h3 className="text-white font-semibold text-sm">Essex Local Guide</h3>
                <p className="text-slate-400 text-[11px]">AI-powered local search</p>
              </div>
            </div>
            <button onClick={() => setIsOpen(false)} className="text-slate-400 hover:text-white transition-colors" data-testid="chatbot-close">
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${
                    msg.role === 'user'
                      ? 'bg-[#0F2C3E] text-white rounded-br-md'
                      : 'bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-200 rounded-bl-md'
                  }`}
                  data-testid={`chatbot-msg-${i}`}
                >
                  <MessageContent content={msg.content} />
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-slate-100 dark:bg-slate-800 px-4 py-3 rounded-2xl rounded-bl-md">
                  <div className="flex gap-1.5">
                    <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Suggestion chips (only when few messages) */}
          {messages.length <= 2 && !loading && (
            <div className="px-4 pb-2 flex flex-wrap gap-1.5">
              {suggestions.map((s, i) => (
                <button
                  key={i}
                  onClick={() => {
                    const msg = s;
                    setInput('');
                    setMessages(prev => [...prev, { role: 'user', content: msg }]);
                    setLoading(true);
                    axios.post(`${API}/api/chatbot/message`, { message: msg, session_id: sessionId })
                      .then(res => {
                        setSessionId(res.data.session_id);
                        setMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
                      })
                      .catch(() => {
                        setMessages(prev => [...prev, { role: 'assistant', content: "Sorry, I couldn't process that." }]);
                      })
                      .finally(() => setLoading(false));
                  }}
                  className="px-3 py-1.5 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 rounded-full text-xs hover:bg-[#D4A017]/10 hover:text-[#D4A017] transition-colors flex items-center gap-1"
                  data-testid={`chatbot-suggestion-${i}`}
                >
                  <MapPin className="h-3 w-3" />{s}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <div className="px-3 py-3 border-t border-slate-200 dark:border-slate-700 flex-shrink-0">
            <div className="flex items-center gap-2 bg-slate-50 dark:bg-slate-800 rounded-xl px-3 py-1">
              <input
                ref={inputRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask about Essex businesses..."
                className="flex-1 bg-transparent border-0 outline-none text-sm text-slate-800 dark:text-white placeholder:text-slate-400 py-2"
                data-testid="chatbot-input"
              />
              <button
                onClick={sendMessage}
                disabled={!input.trim() || loading}
                className="w-8 h-8 bg-[#D4A017] rounded-lg flex items-center justify-center text-[#0F2C3E] disabled:opacity-40 hover:bg-[#F5B728] transition-colors flex-shrink-0"
                data-testid="chatbot-send"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Parse bold (**text**) and listing URLs in the response
const MessageContent = ({ content }) => {
  const parts = content.split(/(\*\*.*?\*\*)/g);
  return (
    <span>
      {parts.map((part, i) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return <strong key={i}>{part.slice(2, -2)}</strong>;
        }
        return <span key={i}>{part}</span>;
      })}
    </span>
  );
};

export default ChatbotWidget;
